import React, { useState, useEffect, useMemo } from 'react';
import { Button, DatePicker, Dropdown, Menu, Radio, Popover, Badge, Icon, Checkbox } from 'antd';
import moment from 'moment';
import 'moment/locale/pt-br';

import Layout from '~/components/UI/Layout/Layout';
import Grid from '~/_domains/calendarTasks/Grid';
import List from '~/_domains/calendarTasks/List/List';
import { department } from '~/components/RegisterTasks/Utils';

import locale from '~/components/UI/DatePicker/Locale';
import { connect } from 'react-redux';
import { SporadictDocumentUpload } from '~/_domains/calendarTasks/Drawers/SporadicDocumentUpload';
import { SporadicDocumentUploadInBatch } from '~/_domains/calendarTasks/Drawers/SporadicDocumentUploadInBatch';
import Tasks from './components/Tasks'; // Updated import path
import { ModuleHeader } from '~/components/ModuleHeader';
import { PendingDocumentsDrawer } from '~/containers/Documents/components/PendingDocumentsDrawer';
import * as calendarTasksActions from '~/_domains/calendarTasks/actions';
import { FaTh, FaList } from 'react-icons/fa';
import Search from '~/_shared/components/Search';
import FilterPillsBar from '~/_shared/components/FilterPillsBar';
import CustomMonthPicker from '~/_shared/components/MonthPicker';
import RestoredFiltersNotice from '~/_shared/components/RestoredFiltersNotice';
import usePersistedFilters from '~/_shared/hooks/usePersistedFilters';
import {
    ALL_STATUSES,
    getCalendarFiltersKey,
    getDefaultCalendarFilters,
    normalizeMonth,
    toggleStatus,
    isDefaultStatuses,
    getCalendarFilterCount,
    isMonthLoading,
} from './filters';

const TASKS_VISUALIZATION_MODE = 'tasks_visualization_mode';
const STATUS_LABELS = { 0: 'Atrasadas', 1: 'Pendentes', 2: 'Concluídas' };

const ScreensCalendarTask = (props) => {
    // Filtros aplicados: sobrevivem ao refresh por alguns minutos, por calendário.
    const persisted = usePersistedFilters(
        getCalendarFiltersKey(props.match.params.id, props.match.params.department),
        getDefaultCalendarFilters
    );
    const applied = persisted.filters;
    const setApplied = persisted.setFilters;
    const defaults = persisted.defaults;
    const month = normalizeMonth(applied.month, defaults.month);
    const actived = Array.isArray(applied.actived) ? applied.actived : ALL_STATUSES;
    const searchTerm = applied.search || '';
    // Mesmo valor que as telas filhas recebiam antes (loadCalendarTasks
    // mutava o estado para o fim do mês).
    const date = useMemo(() => moment(month, 'YYYY-MM').endOf('month'), [month]);

    const [grid, setGrid] = useState(null);
    const [list, setList] = useState(null);
    const [documentDrawer, setDocumentDrawer] = useState({ isOpen: false });
    const [pendingDocumentsDrawer, setPendingDocumentsDrawer] = useState({ isOpen: false });
    const [documentDrawerInBatch, setDocumentDrawerInBatch] = useState({ isOpen: false });
    const [visualizationMode, setVisualizationMode] = useState(localStorage.getItem(TASKS_VISUALIZATION_MODE) || 'box');
    const [filteredTasks, setFilteredTasks] = useState([]);

    const setSearchTerm = (value) => setApplied({ search: value });
    const setActived = (value) => setApplied({ actived: value });
    const resetMonth = () => setApplied({ month: defaults.month });

    const toggleDrawer = (drawerSetter, isOpen) => drawerSetter({ isOpen });
    const handleSearchChange = (e) => setSearchTerm(e.target.value);
    const handleDateChange = (value) => {
        if (value) {
            setApplied({ month: moment(value, 'YYYY-MM').format('YYYY-MM') });
        }
    };
    const handleVisualizationModeChange = ({ target }) => {
        setVisualizationMode(target.value);
        localStorage.setItem(TASKS_VISUALIZATION_MODE, target.value);
    };
    const handleActivedChange = (e) => {
        setActived(toggleStatus(actived, e.target.value));
    };

    // Carga do mês aplicado (restaurado ou padrão) ao montar e a cada troca.
    // Vai um moment novo porque loadCalendarTasks muta a data (endOf).
    useEffect(() => {
        props.loadCalendarTasks(props.match.params.id, moment(month, 'YYYY-MM'));
    }, [month, props.match.params.id, props.loadCalendarTasks]);

    const isCalendarTasksLoading = isMonthLoading(props.calendarTasks, props.match.params.id, month);
    const isMonthDefault = month === defaults.month;
    const filterCount = getCalendarFilterCount({ month: month, actived: actived, search: searchTerm }, defaults, visualizationMode);

    useEffect(() => {
        const departmentId = props.match.params.id;
        const formattedDate = date.format('YYYY/MM');
        const tasks = (props.calendarTasks[departmentId] && 
                      props.calendarTasks[departmentId][formattedDate] && 
                      props.calendarTasks[departmentId][formattedDate].data) || [];

        const filtered = tasks.filter((task) => {
            const isCompleted = task.pendent_tasks_count === 0;
            const isPending = task.pendent_tasks_count > 0;
            const from = moment(task.due_date);
            const to = moment();
            const isOverdue = to.diff(from, 'days') > 0 && isPending;

            // Filter logic:
            // 0 = Atrasadas (Overdue) - task is pending and past due date
            // 1 = Pendentes (Pending) - task is pending but not past due date
            // 2 = Concluidas (Completed) - all tasks are executed

            // Only apply active filters in list view mode
            if (visualizationMode === 'list') {
                if (!actived.includes(2) && isCompleted) return false;
                if (!actived.includes(0) && isOverdue) return false;
                if (!actived.includes(1) && isPending && !isOverdue) return false;
            }

            return task.name.toLowerCase().includes(searchTerm.toLowerCase());
        });

        setFilteredTasks(filtered);
    }, [searchTerm, actived, props.calendarTasks, date, props.match.params.id, visualizationMode]);

    // Resumo do aviso de filtros mantidos: só o que está aplicado e difere do padrão.
    const restoredSummaryParts = [];
    if (!isMonthDefault) restoredSummaryParts.push(date.format('MMM/YYYY'));
    if (visualizationMode === 'list' && !isDefaultStatuses(actived)) {
        restoredSummaryParts.push(actived.map(function(v) { return STATUS_LABELS[v]; }).join(', '));
    }
    if (searchTerm) restoredSummaryParts.push('"' + searchTerm + '"');
    const restoredSummary = restoredSummaryParts.join(' · ');

    return (
        <Layout>
                <ModuleHeader
                    breadcrumbs={['Tarefas', department(props.match.params.id)]}
                    title={department(props.match.params.id)}
                    search={
                        <Search
                            value={searchTerm}
                            placeholder="Buscar tarefas"
                            onChange={handleSearchChange}
                        />
                    }
                    center={
                        <>
                            <Popover
                                trigger="click"
                                placement="bottomRight"
                                content={
                                    <div style={{ width: 320 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                                            <span style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>Filtros</span>
                                            {!persisted.isDefault && (
                                                <a
                                                    style={{ fontSize: 12, color: '#f5222d' }}
                                                    onClick={persisted.resetFilters}
                                                >
                                                    Limpar tudo
                                                </a>
                                            )}
                                        </div>
                                        <div style={{ marginBottom: 14 }}>
                                            <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Mês</div>
                                            <CustomMonthPicker
                                                value={month}
                                                onChange={handleDateChange}
                                                disabled={props.isTasksContextLoading}
                                                showTodayButton={false}
                                                style={{ display: 'block', width: '100%' }}
                                            />
                                        </div>
                                        {visualizationMode === 'list' && (
                                            <div>
                                                <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                    {[
                                                        { value: 0, label: 'Atrasadas', color: '#f5222d', desc: 'Tarefas pendentes após o vencimento.' },
                                                        { value: 1, label: 'Pendentes', color: '#d4b106', desc: 'Tarefas aguardando execução.' },
                                                        { value: 2, label: 'Concluídas', color: '#a0d911', desc: 'Tarefas finalizadas.' },
                                                    ].map(function(opt) {
                                                        var isActive = actived.includes(opt.value);
                                                        var count = filteredTasks.filter(function(t) {
                                                            var isCompleted = t.pendent_tasks_count === 0;
                                                            var isPending = t.pendent_tasks_count > 0;
                                                            var isOverdue = moment().diff(moment(t.due_date), 'days') > 0 && isPending;
                                                            if (opt.value === 0) return isOverdue;
                                                            if (opt.value === 1) return isPending && !isOverdue;
                                                            if (opt.value === 2) return isCompleted;
                                                            return false;
                                                        }).length;
                                                        return (
                                                            <div
                                                                key={opt.value}
                                                                onClick={function() { handleActivedChange({ target: { value: opt.value } }); }}
                                                                style={{
                                                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                                    padding: '8px 12px', borderRadius: 6, cursor: 'pointer',
                                                                    border: '1.5px solid ' + (isActive ? opt.color : '#e8e8e8'),
                                                                    background: isActive ? opt.color + '0a' : '#fafafa',
                                                                    transition: 'all 0.2s',
                                                                }}
                                                            >
                                                                <div>
                                                                    <div style={{ fontSize: 13, fontWeight: isActive ? 600 : 400, color: isActive ? '#262626' : '#8c8c8c' }}>
                                                                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: opt.color, display: 'inline-block', marginRight: 8 }} />
                                                                        {opt.label}
                                                                    </div>
                                                                    <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2, marginLeft: 16 }}>{opt.desc}</div>
                                                                </div>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                    <span style={{ fontSize: 13, fontWeight: 600, color: isActive ? opt.color : '#bfbfbf' }}>{count}</span>
                                                                    {isActive && <Icon type="check" style={{ color: opt.color, fontSize: 14 }} />}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                }
                            >
                                <Badge
                                    count={filterCount}
                                    offset={[-4, 0]}
                                    style={{ backgroundColor: '#722ed1' }}
                                >
                                    <Button icon="bars" style={{ borderRadius: 8 }}>Filtros</Button>
                                </Badge>
                            </Popover>
                            <Radio.Group value={visualizationMode} onChange={handleVisualizationModeChange}>
                                <Radio.Button value="box"><FaTh /></Radio.Button>
                                <Radio.Button value="list"><FaList /></Radio.Button>
                            </Radio.Group>
                            <Dropdown
                                trigger={["click"]}
                                overlay={
                                    <Menu>
                                        <Menu.Item key="1" onClick={function() { toggleDrawer(setDocumentDrawer, true); }}>
                                            <Icon type="file" /> Individual
                                        </Menu.Item>
                                        <Menu.Item key="2" onClick={function() { toggleDrawer(setDocumentDrawerInBatch, true); }}>
                                            <Icon type="copy" /> Em lote
                                        </Menu.Item>
                                        <Menu.Item key="3" onClick={function() { toggleDrawer(setPendingDocumentsDrawer, true); }}>
                                            <Icon type="clock-circle" /> Pendentes
                                        </Menu.Item>
                                    </Menu>
                                }
                            >
                                <Button icon="plus" shape="circle" />
                            </Dropdown>
                        </>
                    }
                    advanced={
                        (function() {
                            var STATUS_MAP = { 0: { label: 'Atrasadas', color: '#f5222d' }, 1: { label: 'Pendentes', color: '#d4b106' }, 2: { label: 'Concluídas', color: '#a0d911' } };
                            var pills = [
                                { key: 'month', label: date.format('MMM/YYYY'), color: '#1890ff', icon: 'calendar',
                                  onRemove: !isMonthDefault ? resetMonth : undefined },
                            ];
                            if (visualizationMode === 'list') {
                                actived.forEach(function(v) {
                                    var s = STATUS_MAP[v];
                                    if (s) {
                                        pills.push({
                                            key: 'status-' + v, label: s.label, color: s.color,
                                            onRemove: actived.length > 1 ? function() { setActived(actived.filter(function(n) { return n !== v; })); } : undefined,
                                        });
                                    }
                                });
                            }
                            if (searchTerm) {
                                pills.push({ key: 'search', label: searchTerm, color: '#722ed1', icon: 'search', onRemove: function() { setSearchTerm(''); } });
                            }
                            return React.createElement(FilterPillsBar, {
                                pills: pills,
                                onClearAll: !persisted.isDefault ? persisted.resetFilters : undefined,
                            });
                        })()
                    }
                />
            <RestoredFiltersNotice
                visible={persisted.wasRestored && filterCount > 0}
                summary={restoredSummary}
                onReset={persisted.resetFilters}
                onDismiss={persisted.dismissRestored}
            />
            <Tasks
                {...props}
                isCalendarTasksLoading={isCalendarTasksLoading}
                date={date}
                openGrid={setGrid}
                openList={setList}
                filter={actived}
                isListOpen={list !== null}
                visualizationMode={visualizationMode}
                filteredTasks={filteredTasks}
            />
            <Grid 
                {...props}
                isCalendarTasksLoading={isCalendarTasksLoading}
                visible={grid !== null}
                onClose={() => setGrid(null)}
                data={grid}
                date={date}
            />
            <List 
                date={date}
                visible={list !== null}
                onClose={() => setList(null)}
                id={list}
                departmentId={props.match.params.id}
            />
            <SporadictDocumentUpload
                isOpen={documentDrawer.isOpen}
                onClose={() => toggleDrawer(setDocumentDrawer, false)}
                selectedDepartmentId={props.match.params.id}
            />
            <SporadicDocumentUploadInBatch
                isVisible={documentDrawerInBatch.isOpen}
                onClose={() => toggleDrawer(setDocumentDrawerInBatch, false)}
                selectedDepartmentId={props.match.params.id}
            />
            <PendingDocumentsDrawer
                isVisible={pendingDocumentsDrawer.isOpen}
                onClose={() => toggleDrawer(setPendingDocumentsDrawer, false)}
            />
        </Layout>
    );
}

// O mês selecionado vive no estado do componente (filtros persistidos), então
// o loading desse mês é calculado lá (isMonthLoading) e passado aos filhos.
// Antes era lido de ownProps.date, que nunca é passado: refletia sempre o mês atual.
const mapStateToProps = (state) => ({
    calendarTasks: state.calendarTasks,
    task: null,
    tasksFromState: state.tasks.list
});

const mapDispatchToProps = (dispatch) => ({
    loadCalendarTasks: (id, date) => dispatch(calendarTasksActions.loadCalendarTasks(id, date)),
    updateTasksCardsFromList: (customers, taskId) => dispatch(calendarTasksActions.updateTasksCardsFromList(customers, taskId)),
    updateTaskStats: (departmentId, date, taskId, stats) => 
        dispatch(calendarTasksActions.updateTaskStats(departmentId, date, taskId, stats)),
});

// Remonta ao trocar de calendário: cada /calendario/:id/:department tem os seus
// filtros, e o hook só lê o armazenamento ao montar.
const CalendarTaskPage = (props) => (
    <ScreensCalendarTask
        key={getCalendarFiltersKey(props.match.params.id, props.match.params.department)}
        {...props}
    />
);

export default connect(mapStateToProps, mapDispatchToProps)(CalendarTaskPage);
